import React, { useEffect, useRef, useState } from "react";
import { APPS } from "../content";
import AppPanel from "./AppPanel";
import useRafScroll from "../hooks/useRafScroll";

/**
 * The work does not scroll down — it scrolls sideways.
 *
 * A tall section pins its inner stage and converts vertical scroll distance
 * into horizontal travel. Crucially this is *not* scroll-jacking: the browser's
 * own scroll is untouched, so trackpads, wheels, keyboards, scrollbars and
 * momentum all behave exactly as the visitor expects.
 *
 * As each panel takes the stage it recolours the entire document. The studio is
 * monochrome; the products are where colour lives.
 */
export default function Work({ onTheme }) {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const fillRef = useRef(null);
  const [panel, setPanel] = useState(0);
  const [horizontal, setHorizontal] = useState(false);
  const panelRef = useRef(0);

  // Panels: intro, then one per app, then the pipeline card.
  const total = APPS.length + 2;

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 940px)");
    const sync = () => setHorizontal(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useRafScroll(() => {
    const section = sectionRef.current;
    if (!section) return;

    const rect = section.getBoundingClientRect();
    const inside = rect.top < window.innerHeight && rect.bottom > 0;

    if (!horizontal) {
      // Vertical fallback: pick the panel nearest the middle of the viewport.
      if (!inside) return;
      const panels = section.querySelectorAll("[data-panel]");
      let best = 0;
      let bestDist = Infinity;
      panels.forEach((el, i) => {
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - window.innerHeight / 2);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
      commit(best);
      return;
    }

    const travel = section.offsetHeight - window.innerHeight;
    const p = Math.max(0, Math.min(1, -rect.top / travel));

    if (trackRef.current) {
      trackRef.current.style.transform = `translate3d(${-p * (total - 1) * 100}vw, 0, 0)`;
    }
    if (fillRef.current) {
      fillRef.current.style.transform = `scaleX(${p})`;
    }

    commit(Math.min(total - 1, Math.round(p * (total - 1))));
  });

  function commit(next) {
    if (panelRef.current === next) return;
    panelRef.current = next;
    setPanel(next);

    const app = APPS[next - 1];
    if (next === 0) onTheme("studio");
    else if (app) onTheme(app.id);
    else onTheme("studio");
  }

  // Leaving the section in either direction returns the page to the studio.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) {
          panelRef.current = -1;
          onTheme("studio");
        }
      },
      { threshold: 0 }
    );
    io.observe(section);
    return () => io.disconnect();
  }, [onTheme]);

  return (
    <section
      className="work"
      id="work"
      data-act="work"
      ref={sectionRef}
      data-mode={horizontal ? "h" : "v"}
      style={horizontal ? { height: `${total * 100}vh` } : undefined}
    >
      <div className="work__stage">
        <div className="work__track" ref={trackRef}>
          <article
            className="panel panel--intro"
            data-panel
            data-on={panel === 0 || undefined}
            inert={(horizontal && panel !== 0) || undefined}
          >
            <div className="panel__introwrap">
              <span className="mono">03 — Work</span>
              <h2 className="panel__introtitle">
                <span className="display">TWO&nbsp;APPS.</span>
                <span className="serif">Both live.</span>
                <span className="display">ONE&nbsp;IDEA.</span>
              </h2>
              <p className="panel__introbody">
                Each one takes a single loop that runs people&rsquo;s lives and puts a
                wedge in it. Keep scrolling — the site hands the screen over to
                them.
              </p>
              <span className="panel__introcue mono">
                Scroll <i>→</i> horizontal from here
              </span>
            </div>
          </article>

          {APPS.map((app, i) => (
            <div data-panel key={app.id} className="panel-slot">
              <AppPanel
                app={app}
                active={panel === i + 1}
                offstage={horizontal && panel !== i + 1}
              />
            </div>
          ))}

          <article
            className="panel panel--next"
            data-panel
            data-on={panel === total - 1 || undefined}
            inert={(horizontal && panel !== total - 1) || undefined}
          >
            <div className="panel__introwrap">
              <span className="mono">AB—03</span>
              <h2 className="panel__introtitle">
                <span className="display">IN&nbsp;THE</span>
                <span className="serif">workshop</span>
              </h2>
              <p className="panel__introbody">
                A third loop, currently being taken apart. If you have one of your
                own that needs breaking, that is a conversation worth having.
              </p>
              <a className="btn btn--fill" href="#contact" data-lens="TALK">
                <span>Start a conversation</span>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
            </div>
          </article>
        </div>

        <div className="work__hud" aria-hidden="true">
          <span className="work__hud-count mono">
            {String(Math.max(0, panel)).padStart(2, "0")} / {String(total - 1).padStart(2, "0")}
          </span>
          <span className="work__hud-bar">
            <i ref={fillRef} />
          </span>
        </div>
      </div>
    </section>
  );
}
